import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import '../../../styles/App.css';
import DeviceRequestHistory from "./DeviceRequestHistory";
import ScheduleSettings from "./ScheduleSettings";
import { API_BASE_URL } from "../../../config";

// This page used to carry Start/Stop Broadcast buttons that POSTed to
// http://192.168.7.28:8000 -- the agent server, which has been deleted, at a
// hardcoded LAN address that was never reachable from anywhere but one desk.
// There is no inbound command channel by design: a screen polls a signed
// manifest and plays what its approved bookings say. So there is nothing to
// start or stop, and the honest replacement is the settings that DO change
// what plays, plus the evidence of whether the screen is still talking to us.

function relativeTime(iso) {
    if (!iso) return "never";
    const then = new Date(iso);
    if (isNaN(then)) return "unknown";
    const seconds = Math.round((Date.now() - then.getTime()) / 1000);
    if (seconds < 90) return `${Math.max(seconds, 0)}s ago`;
    if (seconds < 5400) return `${Math.round(seconds / 60)} min ago`;
    if (seconds < 172800) return `${Math.round(seconds / 3600)} hours ago`;
    return then.toLocaleString();
}

// The backend marks a device offline after 5 minutes of not polling. The poll
// interval is 60s, so this is many missed polls rather than a blip.
const STALE_AFTER_MS = 5 * 60 * 1000;

// --- what is on the screen right now ---------------------------------------
//
// Derived, not observed. Devices make only outbound requests and never upload
// a screenshot, so this is "given the schedule we are serving and how recently
// it checked in, what should be on the glass". The wording below says so —
// calling it a preview and implying a camera would be worse than useless the
// first time a screen is unplugged and this panel still looks healthy.
//
// The placeholder image is a downscaled copy of the artwork the agent ships
// (wallburst_agent/assets/placeholder-ad.png). Two copies of the same picture
// in two repos: regenerate this one if the artwork changes.
// Mirrors utils/fit.py PLACEMENTS on the backend, which validates the value.
//
// Deliberately a SUBSET of it: the backend also still accepts "storefront" and
// "window" so that screens filed under them before WallBurst moved indoors keep
// a readable label, but neither is offered here, so nothing new is filed under
// them. If a host picks a value this list does not contain, the backend accepts
// it; the list only decides what is offered.
//
// The order is the order a host thinks in — where the customers sit, then where
// they queue, then how it is mounted.
const PLACEMENT_OPTIONS = [
    ["", "Not set"],
    ["waiting", "Waiting area"],
    ["lobby", "Lobby or entrance"],
    ["counter", "Counter or checkout"],
    ["dining", "Dining area"],
    ["wall", "Wall mount"],
    ["totem", "Floor totem"],
    ["other", "Other"],
];

// Mirrors utils/venue.py, which validates all three.
//
// These are the two questions an advertiser is actually choosing between, and
// before them a busy salon and a quiet one looked identical on the browse page.
//
// BANDS, not a number field. A host does not know that 487 people came through
// last week, and a listing that said "487 weekly visitors" would be claiming a
// counter nobody installed. A band is what somebody can answer honestly in two
// seconds, and it is the only shape that does not overstate the precision of a
// self-reported figure.
const VENUE_OPTIONS = [
    ["", "Not set"],
    ["salon", "Salon, barbershop or nail salon"],
    ["studio", "Dance, fitness or yoga studio"],
    ["waiting_room", "Medical, dental or auto waiting area"],
    ["venue", "Event or entertainment venue"],
    ["cafe", "Café or restaurant"],
    ["hotel_lobby", "Hotel or office lobby"],
    ["gym", "Gym"],
    ["shop", "Shop or boutique"],
    ["other", "Somewhere else"],
];

const VISITOR_OPTIONS = [
    ["", "Not set"],
    ["under_100", "Fewer than 100 a week"],
    ["100_250", "100 to 250 a week"],
    ["250_500", "250 to 500 a week"],
    ["500_1000", "500 to 1,000 a week"],
    ["1000_2500", "1,000 to 2,500 a week"],
    ["over_2500", "More than 2,500 a week"],
];

// "" is not "unknown" here, it is "use the usual for this kind of place" --
// the server falls back to the venue type's default and reports that it did.
// That distinction is why the empty option is worded as a choice rather than
// as an absence.
const DWELL_OPTIONS = [
    ["", "Use the usual for this kind of place"],
    ["under_5", "Under 5 minutes"],
    ["5_15", "5 to 15 minutes"],
    ["15_30", "15 to 30 minutes"],
    ["30_60", "30 to 60 minutes"],
    ["over_60", "Over an hour"],
];

function ScreenPreview({ preview, loading }) {
    if (loading && !preview) {
        return <div className="sp sp--empty">Checking…</div>;
    }
    if (!preview) return null;

    const { state, playing } = preview;

    if (state === "unreachable") {
        return (
            <div className="sp">
                <div className="sp__frame sp__frame--dead">
                    <div className="sp__dead-title">Unreachable</div>
                    <div className="sp__dead-note">
                        {preview.last_seen_at
                            ? `No check-in for ${Math.round((preview.seconds_since_seen || 0) / 60)} min.`
                            : "This screen has never checked in."}
                    </div>
                </div>
                <p className="sp__caption">
                    Nothing is known about what is on this screen. Anything sold for
                    now is not running.
                </p>
            </div>
        );
    }

    // Outside the screen's hours the agent shows nothing at all — not the
    // placeholder. Falling through to the placeholder view below would tell
    // the broadcaster a lit screen is advertising itself in a dark shop.
    if (state === "off_air") {
        return (
            <div className="sp">
                <div className="sp__frame sp__frame--dead">
                    <div className="sp__dead-title">Off air</div>
                    <div className="sp__dead-note">
                        {preview.next_on_air_at
                            ? `Back on ${new Date(preview.next_on_air_at + "Z").toLocaleString()}.`
                            : "No on-air hours in the coming week."}
                    </div>
                </div>
                <p className="sp__caption">
                    Outside this screen's hours, so it is dark. Bookings resume
                    when it comes back on air.
                </p>
            </div>
        );
    }

    if (state === "playing" && playing) {
        return (
            <div className="sp">
                <div className="sp__frame">
                    <img
                        src={`${API_BASE_URL}${playing.image_url}`}
                        alt="The creative currently scheduled on this screen"
                        className="sp__img"
                    />
                </div>
                <p className="sp__caption">
                    Booking #{playing.request_id}
                    {playing.slot_ends_at &&
                        ` · until ${new Date(playing.slot_ends_at).toLocaleString()}`}
                </p>
            </div>
        );
    }

    return (
        <div className="sp">
            <div className="sp__frame">
                <img
                    src="/placeholder-preview.png"
                    alt="The placeholder shown while this screen is unbooked"
                    className="sp__img"
                />
                <div className="sp__overlay">
                    {preview.public_code && <span>SCREEN {preview.public_code}</span>}
                    {preview.daily_rate != null && preview.daily_rate > 0 && (
                        <span>${Number(preview.daily_rate).toFixed(2)} per day</span>
                    )}
                </div>
            </div>
            <p className="sp__caption">
                Nothing is booked for right now, so the screen is showing the
                placeholder.
            </p>
        </div>
    );
}


export default function VendorDeviceDashboard({ deviceId }) {
    const [device, setDevice] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ name: "", location: "", daily_rate: "", placement: "", orientation: "", venue_type: "", visitors_band: "", dwell_band: "", enabled: false });
    // What the server fell back to for dwell when the host has not overridden
    // it -- "venue" means the figure on the listing is the category default,
    // not something this host asserted. Shown so they can see what advertisers
    // are being told on their behalf.
    const [dwellSource, setDwellSource] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);

    const token = localStorage.getItem("token");

    const loadDevice = useCallback(async () => {
        if (!deviceId) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/devices/${deviceId}`, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            if (!res.ok) throw new Error("Failed to load this screen.");
            const data = await res.json();
            const d = data.device || null;
            setDevice(d);
            if (d) {
                setForm({
                    name: d.name || "",
                    location: d.location || "",
                    daily_rate: d.daily_rate != null ? String(d.daily_rate) : "",
                    placement: d.placement || "",
                    orientation: d.orientation || "",
                    venue_type: d.venue_type || "",
                    visitors_band: d.visitors_band || "",
                    dwell_band: d.dwell_band || "",
                    enabled: !!d.enabled,
                });
                setDwellSource(d.dwell_source || null);
            }
            setError("");
        } catch (err) {
            setError(err.message || "Failed to load this screen.");
        }
        setLoading(false);
    }, [deviceId, token]);

    useEffect(() => { loadDevice(); }, [loadDevice]);

    // --- emergency stop ----------------------------------------------------
    // Deliberately the first control on the page and styled as a danger
    // action: the moment a broadcaster needs it, they are looking at something
    // on a wall in their shop and do not want to hunt through a settings form.
    const [stopBusy, setStopBusy] = useState(false);
    const [stopNote, setStopNote] = useState("");

    const stopped = !!(device && device.broadcast_paused_at);

    async function toggleBroadcast() {
        if (!device) return;
        const action = stopped ? "resume" : "stop";
        let reason = null;
        if (action === "stop") {
            // Not required by the API -- an emergency lever should never be
            // gated behind a form -- but asked for, because "why is A003 off"
            // is the next question and nobody remembers a week later.
            reason = window.prompt(
                "Stop broadcasting on this screen?\n\n" +
                "It will show the placeholder until you resume. Bookings are " +
                "not cancelled or refunded by this.\n\nReason (optional):"
            );
            if (reason === null) return;   // cancelled the dialog
        }
        setStopBusy(true);
        setStopNote("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/vendor/devices/${device.id}/${action}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(reason ? { reason } : {}),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "That did not work.");
            // The server's message, not ours: it knows whether the screen is
            // reachable, and "it will stop within a minute" and "it is offline
            // so unplug it" are very different things to be told.
            setStopNote(data.message || "");
            await loadDevice();
        } catch (err) {
            setStopNote(err.message);
        } finally {
            setStopBusy(false);
        }
    }


    // What is on the screen now. Polled every 30s: the device itself checks in
    // about once a minute, so anything faster reports the same answer twice.
    // Cancelled on unmount, or a slow response lands after navigation and sets
    // state on a component that is gone.
    useEffect(() => {
        if (!deviceId) return;
        let cancelled = false;

        async function load() {
            try {
                const res = await fetch(
                    `${API_BASE_URL}/api/vendor/devices/${deviceId}/preview`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!res.ok) throw new Error("preview unavailable");
                const data = await res.json();
                if (!cancelled) setPreview(data);
            } catch {
                // Leave the last good answer up rather than blanking the panel;
                // a failed poll is not evidence about the screen.
                if (!cancelled) setPreview((current) => current);
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        }

        load();
        const timer = setInterval(load, 30000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [deviceId, token]);

    // There is no per-device notifications endpoint -- this page called
    // /api/vendor/devices/<id>/notifications, which 404s. The real one returns
    // the vendor's notifications with a device_id on each, so filter here.
    useEffect(() => {
        if (!deviceId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/vendor/notifications`, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                setNotifications(
                    (data.notifications || []).filter((n) => String(n.device_id) === String(deviceId))
                );
            } catch {
                /* notifications are informational; failing to load them is not fatal */
            }
        })();
        return () => { cancelled = true; };
    }, [deviceId, token]);

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setMessage("");
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/devices/${deviceId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: form.name,
                    location: form.location,
                    // An empty box clears the rate, which un-lists the screen.
                    // The server treats null as "no rate", not as zero.
                    daily_rate: form.daily_rate === "" ? null : form.daily_rate,
                    placement: form.placement,
                    orientation: form.orientation,
                    venue_type: form.venue_type,
                    visitors_band: form.visitors_band,
                    dwell_band: form.dwell_band,
                    enabled: form.enabled,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "Could not save these settings.");
                setSaving(false);
                return;
            }
            const saved = data.device || {};
            setDevice((current) => ({ ...current, ...saved }));
            setForm({
                name: saved.name || "",
                location: saved.location || "",
                daily_rate: saved.daily_rate != null ? String(saved.daily_rate) : "",
                placement: saved.placement || "",
                orientation: saved.orientation || "",
                venue_type: saved.venue_type || "",
                visitors_band: saved.visitors_band || "",
                dwell_band: saved.dwell_band || "",
                enabled: !!saved.enabled,
            });
            setDwellSource(saved.dwell_source || null);
            setMessage("Saved.");
        } catch {
            setError("Network error. Nothing was saved.");
        }
        setSaving(false);
    }

    if (!token) return <Navigate to="/login" />;
    if (loading) return <div style={{ padding: "2rem" }}>Loading screen…</div>;
    if (!deviceId || (!device && error)) {
        return <div style={{ padding: "2rem", color: "#ea476e" }}>{error || "Screen not found."}</div>;
    }
    if (!device) return <div style={{ padding: "2rem" }}>Screen not found.</div>;

    const lastSeenMs = device.last_seen_at ? Date.now() - new Date(device.last_seen_at).getTime() : null;
    const stale = lastSeenMs === null || lastSeenMs > STALE_AFTER_MS;
    const unread = notifications.filter((n) => !n.is_read);

    return (
        <div style={{ maxWidth: 640, margin: "2rem auto", padding: "1.5rem", background: "#f9f9f9", borderRadius: 8 }}>
            {/* This page used to render underneath the screens list, so there
                was always a way back in view. It is its own route now, and on a
                phone the browser's back gesture is not discoverable enough to
                be the only exit. */}
            <Link to="/vendor-dashboard/devices" className="wb-btn wb-btn--ghost wb-btn--sm">
                ← All my screens
            </Link>

            <h2 style={{ marginTop: "0.75rem" }}>
                {device.public_code && (
                    <code className="device-code" style={{ marginRight: "0.5rem" }}>
                        {device.public_code}
                    </code>
                )}
                {device.name || "unnamed screen"}
            </h2>

            {/* Emergency stop. Above everything else on the page, because the
                situation it exists for is "there is something on my wall right
                now" and a control you have to scroll to is not a kill switch. */}
            <section className={"bc" + (stopped ? " bc--stopped" : "")}>
                <div className="bc__row">
                    <div>
                        <strong className="bc__state">
                            {device.revoked_at
                                ? "Deregistered"
                                : stopped
                                    ? "Broadcasting stopped"
                                    : device.enabled
                                        ? "Broadcasting"
                                        : "Not activated"}
                        </strong>
                        <div className="bc__hint">
                            {device.revoked_at
                                ? "This screen no longer receives a schedule and cannot re-enroll."
                                : stopped
                                    ? `Showing the placeholder${
                                        device.broadcast_pause_reason
                                            ? ` — ${device.broadcast_pause_reason}`
                                            : ""
                                    }. Bookings are untouched.`
                                    : "Approved bookings play on this screen."}
                        </div>
                    </div>

                    {!device.revoked_at && (
                        <button
                            type="button"
                            className={"wb-btn " + (stopped ? "wb-btn--primary" : "wb-btn--danger")}
                            onClick={toggleBroadcast}
                            disabled={stopBusy}
                        >
                            {stopBusy
                                ? "Working…"
                                : stopped
                                    ? "Resume broadcasting"
                                    : "Stop broadcasting"}
                        </button>
                    )}
                </div>

                {/* The server's own words: it is the only side that knows
                    whether the screen is reachable, and "within a minute" and
                    "not until it reconnects — unplug it" are different facts. */}
                {stopNote && <div className="bc__note">{stopNote}</div>}
            </section>

            <ScreenPreview preview={preview} loading={previewLoading} />

            {/* --- what the screen is actually doing ------------------------- */}
            <section style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span
                        aria-hidden="true"
                        style={{
                            width: 10, height: 10, borderRadius: "50%",
                            background: stale ? "#ea476e" : "#2ecc40", display: "inline-block",
                        }}
                    />
                    <strong>{stale ? "Not checking in" : "Checking in"}</strong>
                    <span style={{ color: "#666" }}>last seen {relativeTime(device.last_seen_at)}</span>
                </div>
                {stale && (
                    <div style={{ color: "#ea476e", fontSize: "0.9rem", marginBottom: 8 }}>
                        This screen has not fetched its schedule recently. Nothing will play on it,
                        and a booking made now may not run.
                    </div>
                )}
                <div style={{ color: "#555", fontSize: "0.92rem", lineHeight: 1.7 }}>
                    <div>Enrollment: {device.enrollment_state || "unknown"}
                        {device.revoked_at && <span style={{ color: "#ea476e" }}> (revoked)</span>}</div>
                    <div>Reported status: {device.status || "unknown"}</div>
                    {/* The code is what the screen displays while unbooked,
                        so it is the identifier to quote in support. The serial
                        is the hardware's, and means nothing to anyone else. */}
                    <div>Screen code: {device.public_code || "not assigned"}</div>
                    <div>Serial: {device.serial_number}</div>
                    <div>Agent version: {device.client_version || "unknown"}</div>
                    <div>Last address: {device.client_ip || "unknown"}</div>
                </div>
            </section>

            {/* --- settings --------------------------------------------------- */}
            <form onSubmit={handleSave} style={{ background: "#fff", padding: "1.25rem", borderRadius: 6, marginBottom: "1.5rem" }}>
                <h3 style={{ marginTop: 0 }}>Screen settings</h3>

                <label style={{ display: "block", marginBottom: 12 }}>
                    <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>Name</div>
                    <input
                        type="text"
                        maxLength={128}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px" }}
                    />
                </label>

                <label style={{ display: "block", marginBottom: 12 }}>
                    <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                        Location — shown to advertisers browsing screens
                    </div>
                    <input
                        type="text"
                        maxLength={128}
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        style={{ width: "100%", padding: "6px 8px" }}
                    />
                </label>

                <label style={{ display: "block", marginBottom: 12 }}>
                    <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                        Daily rate (USD) — what one day on this screen costs
                    </div>
                    <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="e.g. 25.00"
                        value={form.daily_rate}
                        onChange={(e) => setForm({ ...form, daily_rate: e.target.value })}
                        style={{ width: 160, padding: "6px 8px" }}
                    />
                    {!form.daily_rate && (
                        <div style={{ color: "#ea476e", fontSize: "0.85rem", marginTop: 4 }}>
                            Without a rate this screen cannot be booked.
                        </div>
                    )}
                </label>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                    <label style={{ display: "block" }}>
                        <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                            Where it is mounted
                        </div>
                        <select
                            value={form.placement}
                            onChange={(e) => setForm({ ...form, placement: e.target.value })}
                            style={{ padding: "6px 8px" }}
                        >
                            {PLACEMENT_OPTIONS.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </label>
                    <label style={{ display: "block" }}>
                        <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                            Which way up
                        </div>
                        <select
                            value={form.orientation}
                            onChange={(e) => setForm({ ...form, orientation: e.target.value })}
                            style={{ padding: "6px 8px" }}
                        >
                            <option value="">Not set</option>
                            <option value="landscape">Landscape (wider than tall)</option>
                            <option value="portrait">Portrait (taller than wide)</option>
                        </select>
                    </label>
                </div>
                {!form.orientation && (
                    <div style={{ color: "#666", fontSize: "0.85rem", margin: "-4px 0 12px" }}>
                        Set which way up the screen is so advertisers are warned
                        before booking an ad that would not fill it.
                    </div>
                )}

                {/* Who is in the room. This is the whole reason an indoor screen
                    is worth buying, and it is the only thing that distinguishes
                    a busy salon from a quiet one on the browse page. */}
                <div style={{ borderTop: "1px solid #eee", margin: "4px 0 12px", paddingTop: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>Your audience</div>
                    <div style={{ color: "#666", fontSize: "0.85rem", marginBottom: 10 }}>
                        Advertisers choose screens on this. A screen with nothing
                        here shows no audience information at all on the listing.
                    </div>

                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                        <label style={{ display: "block" }}>
                            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                                What kind of place is it?
                            </div>
                            <select
                                value={form.venue_type}
                                onChange={(e) => setForm({ ...form, venue_type: e.target.value })}
                                style={{ padding: "6px 8px" }}
                            >
                                {VENUE_OPTIONS.map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>

                        <label style={{ display: "block" }}>
                            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                                Roughly how many people a week?
                            </div>
                            <select
                                value={form.visitors_band}
                                onChange={(e) => setForm({ ...form, visitors_band: e.target.value })}
                                style={{ padding: "6px 8px" }}
                            >
                                {VISITOR_OPTIONS.map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>

                        <label style={{ display: "block" }}>
                            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: 4 }}>
                                How long does a visit last?
                            </div>
                            <select
                                value={form.dwell_band}
                                onChange={(e) => setForm({ ...form, dwell_band: e.target.value })}
                                style={{ padding: "6px 8px" }}
                            >
                                {DWELL_OPTIONS.map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {/* Said rather than left to be discovered. A host who never
                        touches the dwell field is still publishing a figure --
                        ours, from the venue type -- and they should know which
                        one advertisers are seeing on their behalf. */}
                    {!form.dwell_band && dwellSource === "venue" && (
                        <div style={{ color: "#666", fontSize: "0.85rem", marginBottom: 10 }}>
                            Using the usual length for this kind of place. Change
                            it if visits here are shorter or longer than that.
                        </div>
                    )}

                    {/* The figure is unverified and it raises what this screen
                        can charge. Saying so to the person entering it is the
                        cheapest honest control there is. */}
                    {form.visitors_band && (
                        <div style={{ color: "#666", fontSize: "0.85rem", marginBottom: 10 }}>
                            Shown to advertisers as your own estimate — nothing
                            here counts visitors. Keep it realistic: it affects
                            what your screen is worth, and a booking that does not
                            deliver is how a host loses a repeat advertiser.
                        </div>
                    )}

                    {!form.venue_type && (
                        <div className="wb-note" style={{ fontSize: "0.85rem" }}>
                            Until you set the kind of place, this screen is listed
                            with no audience information — which is the least
                            attractive thing on the browse page.
                        </div>
                    )}
                </div>

                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <input
                        type="checkbox"
                        checked={form.enabled}
                        onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    />
                    <span>Available for booking</span>
                </label>

                <button
                    type="submit"
                    disabled={saving}
                    style={{
                        padding: "0.5rem 1.25rem", background: "#ea476e", color: "#fff",
                        border: "none", borderRadius: 4, cursor: saving ? "default" : "pointer",
                    }}
                >
                    {saving ? "Saving…" : "Save settings"}
                </button>
                {message && <span style={{ marginLeft: 12, color: "#2ecc40" }}>{message}</span>}
                {error && <div style={{ marginTop: 10, color: "#ea476e" }}>{error}</div>}
            </form>

            {unread.length > 0 && (
                <section style={{ marginBottom: "1.5rem" }}>
                    <h3>Notifications</h3>
                    <ul style={{ paddingLeft: "1.2rem" }}>
                        {unread.map((n) => (
                            <li key={n.id} style={{ marginBottom: 4 }}>{n.message}</li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Above the request history on purpose: the hours decide what
                can appear in that list, so reading them in the other order
                makes the gaps look like a bug. */}
            <ScheduleSettings deviceId={deviceId} />

            <DeviceRequestHistory deviceId={deviceId} />

            {/* Deregistering is deliberately NOT offered here. The endpoint is
                still live (POST /api/vendor/devices/<id>/revoke) and still
                does the right thing, but it is permanent, it cancels and
                refunds every unfinished booking, and nothing in this UI can
                undo it -- recovery needs physical access to the hardware to
                re-key it, and comes back as a different screen with a new
                code and no history. That is the answer to stolen hardware,
                not something a host should be one confirm dialog away from
                while looking for a way to take a screen offline for the
                afternoon. Stop broadcasting, at the top of this page, is the
                lever for that. Revisit when there is a support path behind
                it. */}
        </div>
    );
}
